<template>
  <el-dialog
    :model-value="visible"
    @update:model-value="(val) => $emit('update:visible', val)"
    title="导出"
    width="500px"
    :close-on-click-modal="false"
    @close="close"
  >
    <el-form label-width="100px" label-position="left">
      <el-form-item label="文件名">
        <el-input v-model="fileName" placeholder="请输入文件名" style="width: 100%;" />
      </el-form-item>
      <el-form-item label="选择数据">
        <el-radio-group v-model="dataScope">
          <el-radio :value="1">当前页数据</el-radio>
          <el-radio :value="2">全部数据</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="选择字段">
        <div style="width: 100%;">
          <el-checkbox v-model="allChecked" @change="toggleAll">全选</el-checkbox>
          <div style="display: flex; flex-wrap: wrap; margin-top: 8px;">
            <el-checkbox
              v-for="field in fields"
              :key="field.key"
              v-model="field.checked"
              style="width: 33%; margin-bottom: 8px;"
            >
              {{ field.label }}
            </el-checkbox>
          </div>
        </div>
      </el-form-item>
      <el-form-item label="其它选项">
        <el-checkbox v-model="withHeader">表头</el-checkbox>
        <el-checkbox v-model="withFooter" style="margin-left: 16px;">表尾</el-checkbox>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="close">取消</el-button>
      <el-button type="primary" @click="doExport" :loading="loading">确定</el-button>
    </template>
  </el-dialog>
</template>

<script>
export default {
  props: {
    visible: { type: Boolean, default: false },
    fields: { type: Array, required: true },
    exportApi: { type: Function, required: true },
    getSearchParams: { type: Function, required: true },
    currentPage: { type: Object, default: () => ({ current: 1, pageSize: 15 }) },
    defaultFileName: { type: String, default: '' }, 
  },
  emits: ['update:visible', 'success'],
  data() {
    return {
      fileName: '',
      dataScope: 1,
      allChecked: true,
      withHeader: true,
      withFooter: false,
      loading: false,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        // ★ 1. 优化：文件名智能生成逻辑
        this.initFileName();
        
        this.dataScope = 1;
        this.allChecked = true;
        this.fields.forEach(f => f.checked = true);
        this.withHeader = true;
        this.withFooter = false;
      }
    },
    fields: {
      handler() {
        this.allChecked = this.fields.every(f => f.checked);
      },
      deep: true,
    },
  },
  methods: {
    // ★ 新增：智能生成文件名（如果父组件没传，或者传了 index数据，就自动解析）
    initFileName() {
      // 1. 优先使用父组件传递的文件名（但要过滤掉不合理的默认值）
      if (this.defaultFileName && this.defaultFileName !== 'index数据' && this.defaultFileName !== '数据导出') {
        this.fileName = this.defaultFileName;
        return;
      }

      // 2. 如果没有父组件传值，根据当前路由信息动态生成
      const routeTitle = this.$route.meta?.title || this.$route.name || '';
      if (routeTitle) {
        // 替换末尾的“管理”为“数据”，如果不是以“管理”结尾，则追加“数据”
        let baseName = routeTitle.replace(/管理$/, '');
        if (!baseName.endsWith('数据')) {
          baseName += '数据';
        }
        // 处理小写/英文路由名的兼容
        baseName = baseName.charAt(0).toUpperCase() + baseName.slice(1);
        this.fileName = baseName;
      } else {
        // 3. 最终兜底
        this.fileName = '数据导出';
      }
    },

    toggleAll(val) {
      this.fields.forEach(f => f.checked = val);
    },
    close() {
      this.$emit('update:visible', false);
    },
    async doExport() {
      const selected = this.fields.filter(f => f.checked).map(f => f.key);
      if (selected.length === 0) {
        this.$message.warning('请至少选择一个字段');
        return;
      }
      this.loading = true;
      try {
        const searchParams = this.getSearchParams();
        const params = {
          fileName: this.fileName || '数据导出',
          scope: this.dataScope,
          fields: selected,
          withHeader: this.withHeader,
          withFooter: this.withFooter,
          page: this.dataScope === 1 ? this.currentPage.current : undefined,
          pageSize: this.dataScope === 1 ? this.currentPage.pageSize : undefined,
          searchParams,
        };
        
        const response = await this.exportApi(params);

        // ★ 2. 优化：后端错误处理机制（防止下载 1KB 损坏文件）
        let fileBlob = null;
        
        // 兼容不同的 Axios 返回封装结构
        if (response instanceof Blob) {
          fileBlob = response;
        } else if (response && response.data instanceof Blob) {
          fileBlob = response.data;
        }

        if (!fileBlob) {
           throw new Error('导出接口未返回有效的文件流');
        }

        // 检查后端是否返回了 JSON 格式的错误信息（Content-Type 为 application/json）
        // 即使 responseType 是 blob，如果后端抛错，Content-Type 可能会变成 application/json
        if (fileBlob.type.includes('application/json')) {
          const errorText = await fileBlob.text();
          try {
            const errorData = JSON.parse(errorText);
            throw new Error(errorData.message || '导出失败，请检查数据或联系管理员');
          } catch (e) {
            throw new Error(errorText || '导出失败，返回数据格式异常');
          }
        }

        // 正常下载
        const link = document.createElement('a');
        link.href = URL.createObjectURL(fileBlob);
        link.download = `${params.fileName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        this.$emit('success');
        this.close();
      } catch (e) {
        console.error('导出失败', e);
        // 给出明确的错误提示，而不是统一提示“导出失败”
        this.$message.error(e.message || '导出失败，请重试');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>